import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Mail, Download, Users, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function NewsletterAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ subscribers: [], total: 0, active: 0, unsubscribed: 0 });
  const [filter, setFilter] = useState('all'); // all, active, unsubscribed

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (response.data.role !== 'admin') {
        // Silently redirect non-admin users
        navigate('/');
      }
    } catch (error) {
      navigate('/login');
    }
  }, [navigate]);

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/newsletter/subscribers`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchSubscribers();
  }, [checkAuth, fetchSubscribers]);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Subscribers exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export subscribers');
    }
  };

  const filteredSubscribers = filter === 'all' 
    ? data.subscribers 
    : data.subscribers.filter(s => s.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading subscribers...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">Newsletter Subscribers</h1>
              <p className="text-base text-slate-600">Manage your weekly teaching tips subscribers</p>
            </div>
            
            <button
              onClick={handleExport}
              className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95 inline-flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-8 h-8 text-blue-600" />
                <span className="text-3xl font-bold text-slate-900">{data.total}</span>
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1">Total Subscribers</h3>
              <p className="text-sm text-slate-600">All time signups</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <Mail className="w-8 h-8 text-green-600" />
                <span className="text-3xl font-bold text-slate-900">{data.active}</span>
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1">Active Subscribers</h3>
              <p className="text-sm text-slate-600">Currently subscribed</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-8 h-8 text-purple-600" />
                <span className="text-3xl font-bold text-slate-900">
                  {data.total > 0 ? Math.round((data.active / data.total) * 100) : 0}%
                </span>
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1">Retention Rate</h3>
              <p className="text-sm text-slate-600">Active vs total</p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-6">
            <div className="flex border-b border-slate-100">
              <button
                onClick={() => setFilter('all')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  filter === 'all'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All ({data.total})
              </button>
              <button
                onClick={() => setFilter('active')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  filter === 'active'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Active ({data.active})
              </button>
              <button
                onClick={() => setFilter('unsubscribed')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  filter === 'unsubscribed'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Unsubscribed ({data.unsubscribed})
              </button>
            </div>

            {/* Subscribers List */}
            <div className="p-6">
              {filteredSubscribers.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No subscribers yet</h3>
                  <p className="text-slate-600">Subscribers will appear here when they sign up</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredSubscribers.map((subscriber, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border border-slate-200 rounded-xl p-4 hover:bg-slate-50 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          subscriber.status === 'active' ? 'bg-green-100' : 'bg-slate-100'
                        }`}>
                          <Mail className={`w-5 h-5 ${
                            subscriber.status === 'active' ? 'text-green-600' : 'text-slate-400'
                          }`} />
                        </div>
                        
                        <div>
                          <p className="font-semibold text-slate-900">{subscriber.email}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(subscriber.subscribed_at).toLocaleDateString()}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              subscriber.status === 'active'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {subscriber.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <span className="text-sm text-slate-500">
                        {subscriber.source || 'website'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-blue-900 mb-3">📧 How to Send Newsletters</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p><strong>Option 1:</strong> Export CSV and import into Mailchimp, ConvertKit, or SendGrid</p>
              <p><strong>Option 2:</strong> Copy emails and paste into your email client (Gmail, Outlook)</p>
              <p><strong>Option 3:</strong> Use the exported list with any email marketing tool</p>
              <p className="mt-4 text-blue-700">💡 <strong>Tip:</strong> Send newsletters weekly on the same day for best engagement!</p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default NewsletterAdmin;
